import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../../components/header/header.component';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { ButtonComponent } from '../../components/button/button.component';
import { PaymentReceiptComponent } from '../../components/payment-receipt/payment-receipt.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { PaymentService } from '../../services/payment.service';
import { ProjectSettingsService } from '../../services/project-settings.service';
import { FormService } from '../../services/form.service';

@Component({
  selector: 'app-payment-list-page',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, AgGridListComponent, SelectInputComponent, SearchInputComponent, ButtonComponent, PaymentReceiptComponent],
  templateUrl: './payment-list-page.component.html',
})
export class PaymentListPageComponent {
  @ViewChild('receipt_modal') receipt_modal: PaymentReceiptComponent | undefined;

  page_name = 'Payment';
  params: any = {};
  items: any = { data: [], count: 0 };

  university_options: any[] = [];
  filtered_academic_years: any[] = [];
  filtered_exams: any[] = [];
  filtered_subjects: any[] = [];

  columns: any[] = [
    {
      headerName: 'Student Name', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const name = p.data?.student_name || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:500;color:#212121">${name}</div>`;
      }
    },
    {
      headerName: 'Roll No.', flex: 1.5, minWidth: 130,
      cellRenderer: (p: any) => {
        const roll = p.data?.barcode || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">${roll}</div>`;
      }
    },
    {
      headerName: 'Subject', flex: 1.5, minWidth: 130,
      cellRenderer: (p: any) => {
        const subject = p.data?.subject || p.data?.Subject?.name || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">${subject}</div>`;
      }
    },
    {
      headerName: 'Amount Paid', flex: 1.2, minWidth: 120,
      cellRenderer: (p: any) => {
        const amount = p.data?.amount != null ? `₹${p.data.amount}/-` : '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:600;color:#212121">${amount}</div>`;
      }
    },
    {
      headerName: 'Payment Method', flex: 1.5, minWidth: 140,
      cellRenderer: (p: any) => {
        const method = p.data?.payment_method || p.data?.method || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">${method}</div>`;
      }
    },
    {
      headerName: 'Date & Time', flex: 1.8, minWidth: 160,
      cellRenderer: (p: any) => {
        const raw = p.data?.payment_date || p.data?.createdAt;
        if (!raw) return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">-</div>`;
        const d = new Date(raw);
        const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">${date} ${time}</div>`;
      }
    },
    {
      headerName: 'Transaction ID', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const txn = p.data?.gateway_transaction_id || p.data?.transaction_id || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">${txn}</div>`;
      }
    },
    {
      headerName: 'Actions', flex: 0.8,
      is_download_column: true,
      sortable: false, filter: false,
      headerClass: 'custom-header-center',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: () => `
        <button style="background:#EAEBFF;border:none;border-radius:50%;cursor:pointer;width:36px;height:36px;display:flex;align-items:center;justify-content:center">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3F41D1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>`,
      onCellClicked: (p: any) => this.download_receipt(p.data),
      editable: false, pinned: 'right', width: 100, minWidth: 100, maxWidth: 100
    },
  ];

  constructor(
    public gs: GlobalService,
    public dds: DropdownService,
    public payment_service: PaymentService,
    public ps: ProjectSettingsService,
    public fs: FormService,
  ) { }

  async ngOnInit() {
    // Access Level: lock the filters this user cannot change before the first fetch.
    this.gs.apply_access_scope_to_params(this.params);
    if (!this.gs.user_role_data?.payment_download_receipt) {
      this.columns = this.columns.filter((c: any) => !c.is_download_column);
    }
    await this.dds.get_dropdown_list(['assessment_data']);
    this.university_options = this.dds.dropdown_list?.assessment_university_list || [];
    this.patch_latest_assessment();
    this.update_filtered_options();
    await this.get_list();
  }

  patch_latest_assessment() {
    const all_assessments: any[] = this.dds.dropdown_list?.assessment_data_list || [];
    if (!all_assessments.length) return;
    const belongs_to_all = this.gs.access_scope.is_all;
    const assessments = belongs_to_all
      ? all_assessments
      : all_assessments.filter((a: any) => a.University?.id == this.params.university_id);
    if (!assessments.length) return;
    const latest = assessments.reduce((prev: any, curr: any) => (curr.id > prev.id ? curr : prev));
    if (belongs_to_all) {
      this.params.university_id = latest.University?.id || this.params.university_id || null;
    }
    this.params.academic_year_id = latest.AcademicYear?.id || null;
    this.params.exam_id = latest.Exam?.id || null;
    this.params.subject_id = latest.Subject?.id || null;
  }

  update_filtered_options() {
    const data: any[] = this.dds.dropdown_list?.assessment_data_list || [];
    const uni_id = this.params.university_id;
    const ay_id = this.params.academic_year_id;
    const exam_id = this.params.exam_id;

    const ay_seen = new Set();
    this.filtered_academic_years = data
      .filter((x: any) => !uni_id || x.University?.id == uni_id)
      .reduce((acc: any[], x: any) => {
        const id = x.AcademicYear?.id;
        if (id && !ay_seen.has(id)) { ay_seen.add(id); acc.push({ id, title: x.AcademicYear?.name || 'none', value: id }); }
        return acc;
      }, []);

    const exam_seen = new Set();
    this.filtered_exams = data
      .filter((x: any) => (!uni_id || x.University?.id == uni_id) && (!ay_id || x.AcademicYear?.id == ay_id))
      .reduce((acc: any[], x: any) => {
        const id = x.Exam?.id;
        if (id && !exam_seen.has(id)) {
          exam_seen.add(id);
          acc.push({ id, title: (x.Exam?.name || 'none') + (x.Exam?.code ? ` (${x.Exam.code})` : ''), value: id });
        }
        return acc;
      }, []);

    const sub_seen = new Set();
    this.filtered_subjects = data
      .filter((x: any) =>
        (!uni_id || x.University?.id == uni_id) &&
        (!ay_id || x.AcademicYear?.id == ay_id) &&
        (!exam_id || x.Exam?.id == exam_id)
      )
      .reduce((acc: any[], x: any) => {
        const id = x.Subject?.id;
        if (id && !sub_seen.has(id)) {
          sub_seen.add(id);
          acc.push({ id, title: (x.Subject?.name || 'none') + (x.Subject?.code ? ` (${x.Subject.code})` : ''), value: id });
        }
        return acc;
      }, []);
  }

  on_university_change() {
    this.params.academic_year_id = null;
    this.params.exam_id = null;
    this.params.subject_id = null;
    this.update_filtered_options();
    this.apply_filters();
  }

  on_academic_year_change() {
    this.params.exam_id = null;
    this.params.subject_id = null;
    this.update_filtered_options();
    this.apply_filters();
  }

  on_exam_change() {
    this.params.subject_id = null;
    this.update_filtered_options();
    this.apply_filters();
  }

  apply_filters() {
    this.get_list({ page: 1 });
  }

  async get_list(extra?: any) {
    try {
      const res: any = await this.payment_service.get_list({ ...this.params, ...extra, status: 'success' });
      this.items = res;
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to fetch payments', 'Error', 'error');
    }
  }

  async download_receipt(item: any) {
    if (!item?.id) return;
    try {
      const res = await this.ps.download_receipt(item.id);
      if (res?.data && this.receipt_modal) {
        this.receipt_modal.receipt_data = res.data;
        this.receipt_modal.print_receipt();
      } else {
        this.gs.toastr_shows_function('Receipt data not available.', 'Error', 'error');
      }
    } catch {
      this.gs.toastr_shows_function('Failed to load receipt.', 'Error', 'error');
    }
  }

  async download() {
    await this.fs.bulk_download(
      this.payment_service,
      'get_list',
      [
        { column_name: 'Student Name', type: 'text', return_as: 'student_name' },
        { column_name: 'Roll No.', type: 'text', return_as: 'barcode' },
        { column_name: 'Subject', type: 'text', return_as: 'subject' },
        { column_name: 'Amount Paid', type: 'text', return_as: 'amount' },
        { column_name: 'Payment Method', type: 'text', return_as: 'payment_method' },
        { column_name: 'Date & Time', type: 'text', return_as: 'payment_date' },
        { column_name: 'Transaction ID', type: 'text', return_as: 'gateway_transaction_id' },
      ],
      'Payments',
      { ...this.params, status: 'success' }
    );
    this.gs.toastr_shows_function('Payment details imported successfully.', 'Success', 'success');
  }
}
